import { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Check, X, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { DOC_TYPE_LABELS, ASSIGNABLE_DOC_TYPES, type VerificationDocType } from '@/lib/verification/docTypeLabels';

interface QueueRow {
  id: string;
  company_id: string;
  doc_type: string;
  file_name: string;
  storage_path: string;
  parse_confidence: number | null;
  match_results: any;
  discrepancies: any;
  review_status: string;
  created_at: string;
  company_name?: string;
}

type FilterKey = 'in_review' | 'unknown' | 'all';

export default function AdminVerification() {
  const { toast } = useToast();
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('in_review');
  const [decideOpen, setDecideOpen] = useState<{ id: string; action: 'approve' | 'reject' } | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Local pending doc_type reassignments per row (id -> new type)
  const [typeOverrides, setTypeOverrides] = useState<Record<string, VerificationDocType>>({});

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from('company_verification_documents' as any)
      .select('id, company_id, doc_type, file_name, storage_path, parse_confidence, match_results, discrepancies, review_status, created_at')
      .is('superseded_by', null)
      .order('created_at', { ascending: false })
      .limit(200);
    if (filter === 'in_review') q = q.eq('review_status', 'in_review');
    else if (filter === 'unknown') q = q.eq('doc_type', 'unknown');
    const { data } = await q;
    const docs = (data || []) as unknown as QueueRow[];

    if (docs.length) {
      const ids = Array.from(new Set(docs.map((d) => d.company_id)));
      const { data: cos } = await supabase.from('companies').select('id, name').in('id', ids);
      const m = new Map((cos || []).map((c: any) => [c.id, c.name]));
      docs.forEach((d) => { d.company_name = m.get(d.company_id) || '—'; });
    }
    setRows(docs);
    setTypeOverrides({});
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openPreview = async (path: string) => {
    const { data } = await supabase.storage.from('compliance-documents').createSignedUrl(path, 60 * 10);
    if (data?.signedUrl) setPreviewUrl(data.signedUrl);
  };

  const saveType = async (rowId: string, newType: VerificationDocType) => {
    const { error } = await supabase
      .from('company_verification_documents' as any)
      .update({ doc_type: newType } as any)
      .eq('id', rowId);
    if (error) {
      toast({ title: 'Failed to update type', description: error.message, variant: 'destructive' });
      return;
    }
    setRows((r) => r.map((x) => x.id === rowId ? { ...x, doc_type: newType } : x));
    setTypeOverrides((o) => { const { [rowId]: _, ...rest } = o; return rest; });
    toast({ title: 'Document type updated' });
  };

  const decide = async () => {
    if (!decideOpen) return;
    setBusy(true);
    try {
      const docTypeOverride = typeOverrides[decideOpen.id];
      const { error } = await supabase.functions.invoke('decide-verification-document', {
        body: {
          document_id: decideOpen.id,
          decision: decideOpen.action,
          notes,
          ...(docTypeOverride ? { doc_type: docTypeOverride } : {}),
        },
      });
      if (error) throw error;
      toast({ title: `Document ${decideOpen.action}d` });
      setDecideOpen(null);
      setNotes('');
      load();
    } catch (e: any) {
      toast({ title: 'Failed', description: e?.message || 'Try again', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Verification Review Queue</h1>
          <div className="flex gap-2">
            <Button size="sm" variant={filter === 'in_review' ? 'default' : 'outline'} onClick={() => setFilter('in_review')}>
              In Review
            </Button>
            <Button size="sm" variant={filter === 'unknown' ? 'default' : 'outline'} onClick={() => setFilter('unknown')}>
              Unknown type
            </Button>
            <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'} onClick={() => setFilter('all')}>
              All
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-[#9ca3af]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-[#9ca3af]">No documents in queue.</div>
        ) : (
          <div className="rounded-md border border-[#1e2530] bg-[#0d1117]">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1e2530]">
                  <TableHead className="text-[#9ca3af]">Company</TableHead>
                  <TableHead className="text-[#9ca3af]">Doc Type</TableHead>
                  <TableHead className="text-[#9ca3af]">Confidence</TableHead>
                  <TableHead className="text-[#9ca3af]">Discrepancies</TableHead>
                  <TableHead className="text-[#9ca3af]">Status</TableHead>
                  <TableHead className="text-[#9ca3af]">Uploaded</TableHead>
                  <TableHead className="text-[#9ca3af]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const disc = Array.isArray(r.discrepancies) ? r.discrepancies : [];
                  const pendingType = typeOverrides[r.id];
                  const effectiveType = (pendingType || r.doc_type) as string;
                  return (
                    <TableRow key={r.id} className="border-[#1e2530]">
                      <TableCell className="text-white">{r.company_name}</TableCell>
                      <TableCell className="text-white min-w-[180px]">
                        <div className="flex items-center gap-1.5">
                          <Select
                            value={effectiveType}
                            onValueChange={(v) => setTypeOverrides((o) => ({ ...o, [r.id]: v as VerificationDocType }))}
                          >
                            <SelectTrigger className="h-7 text-xs bg-transparent border-[#1e2530] text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ASSIGNABLE_DOC_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>{DOC_TYPE_LABELS[t]}</SelectItem>
                              ))}
                              <SelectItem value="unknown">Detecting…</SelectItem>
                            </SelectContent>
                          </Select>
                          {pendingType && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-success" onClick={() => saveType(r.id, pendingType)}>
                              Save
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-white">{r.parse_confidence != null ? `${Math.round(r.parse_confidence * 100)}%` : '—'}</TableCell>
                      <TableCell className="text-white text-xs">
                        {disc.length ? (
                          <Badge variant="destructive">{disc.length} issue{disc.length > 1 ? 's' : ''}</Badge>
                        ) : (
                          <span className="text-[#9ca3af]">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{r.review_status.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-[#9ca3af] text-xs">
                        {new Date(r.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openPreview(r.storage_path)} title="Preview">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-success" onClick={() => setDecideOpen({ id: r.id, action: 'approve' })} title="Approve">
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDecideOpen({ id: r.id, action: 'reject' })} title="Reject">
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!decideOpen} onOpenChange={(v) => { if (!v) { setDecideOpen(null); setNotes(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{decideOpen?.action === 'approve' ? 'Approve' : 'Reject'} document</DialogTitle>
          </DialogHeader>
          {decideOpen && typeOverrides[decideOpen.id] && (
            <div className="text-xs text-muted-foreground">
              Will save document type as <strong>{DOC_TYPE_LABELS[typeOverrides[decideOpen.id]]}</strong>.
            </div>
          )}
          <Textarea
            placeholder={decideOpen?.action === 'reject' ? 'Reason (visible to company)…' : 'Optional notes…'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecideOpen(null)} disabled={busy}>Cancel</Button>
            <Button onClick={decide} disabled={busy || (decideOpen?.action === 'reject' && !notes.trim())}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : decideOpen?.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewUrl} onOpenChange={(v) => { if (!v) setPreviewUrl(null); }}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader><DialogTitle>Document Preview</DialogTitle></DialogHeader>
          {previewUrl && <iframe src={previewUrl} className="w-full h-full border-0" />}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
